/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MESSAGE_BOX_ICON_VIEW, MessageBox, Overlay, OVERLAY_CONTENT_MOD, OVERLAY_VIEW } from '@veeam/components';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';

import { canViewPortalPages } from '../../../core-modules/base-core-module';
import { useGlobalAppData, useGlobalContext, useGlobalLang, useGlobalServices } from '../../providers/GlobalContextProvider';
import { getPortalContainer } from '../../../helpers/react-helpers';
import { AppStates } from '../../../enums';

import type { BaseLang } from '../../../interfaces';

export interface BaseLayoutProps {
    children: React.ReactElement;
    onLoginButtonClick?: () => void;
}

export const BaseLayout: React.FC<BaseLayoutProps> = observer(({ children, onLoginButtonClick }) => {
    const appDataStore = useGlobalAppData();
    const navigate = useNavigate();
    const location = useLocation();
    const lang = useGlobalLang<BaseLang>();
    const {
        getLoginPath,
        getPortalPath,
        logout,
        appState,
        appEventHandlers,
        suppliers,
        services,
        addons,
        appStorage,
    } = useGlobalContext();
    const { notificationService } = useGlobalServices();
    const [authBlock, setAuthBlock] = useState(false);
    const [destroyedBlock, setDestroyedBlock] = useState(false);
    const handleLoginButtonClick = () => {
        notificationService.closeAll(); // close all notification service dialogs on logout navigation to login
        onLoginButtonClick?.();
        return logout();
    };
    const isLoginPage = () => location.pathname === getLoginPath();
    const canRenderChildren = () => (
        (isLoginPage() && !canViewPortalPages(appState))
        || (!isLoginPage() && canViewPortalPages(appState))
    );

    useEffect(() => {
        if (appState === AppStates.LoggedIn && isLoginPage()) {
            navigate(getPortalPath());
            appEventHandlers.onLoggedIn?.({ appDataStore, services, suppliers, lang, appStorage, addons });
        } else if (appState === AppStates.Unauthorized && !isLoginPage()) {
            navigate(getLoginPath(location.pathname));
        } else if (appState === AppStates.SessionExpired && !destroyedBlock) {
            setAuthBlock(true);
        } else if (appState === AppStates.Destroyed && !authBlock) {
            setDestroyedBlock(true);
        }
    }, [appState]);

    useEffect(() => {
        document.title = appDataStore.portalName;
    }, [appDataStore.portalName]);

    return (
        <>
            {canRenderChildren() && children}

            {(authBlock || destroyedBlock) && createPortal(
                <Overlay
                    fixed
                    contentMod={OVERLAY_CONTENT_MOD.center}
                    view={OVERLAY_VIEW.tone}
                    style={{ zIndex: 10000 }} // because z-index of combobox-menu list is 9999
                >
                    {authBlock && (
                        <MessageBox
                            header={lang.SESSION_EXPIRED}
                            iconView={MESSAGE_BOX_ICON_VIEW.info}
                            actions={[{
                                text: lang.LOG_IN,
                                onClick: () => handleLoginButtonClick(),
                            }]}
                            canClose={false}
                        >
                            {lang.PLEASE_LOG_IN_AGAIN_TO_CONTINUE_USING}
                        </MessageBox>
                    )}

                    {destroyedBlock && (
                        <MessageBox
                            header={lang.ERROR}
                            iconView={MESSAGE_BOX_ICON_VIEW.error}
                            actions={[{
                                text: lang.LOG_IN,
                                onClick: () => {
                                    window.location.reload();
                                },
                            }]}
                            canClose={false}
                        >
                            {lang.WEB_SERVER_IS_NOT_RESPONDING}
                        </MessageBox>
                    )}
                </Overlay>,
                getPortalContainer(),
            )}
        </>
    );
});
